import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import { authService, joinService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { buildLoginPath, buildRegisterPath, getSafeRedirectPath } from '../utils/redirect';

const AVATAR_GRADIENTS = [
  'from-emerald-400 to-teal-600',
  'from-sky-400 to-indigo-600',
  'from-amber-400 to-orange-600',
  'from-rose-400 to-pink-600',
  'from-violet-400 to-purple-600',
  'from-lime-400 to-emerald-600',
];

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}

function pickGradient(seed) {
  if (!seed) return AVATAR_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

function StatePill({ icon, tone, children }) {
  const toneClasses = tone === 'error'
    ? 'bg-error-container text-on-error-container'
    : 'bg-secondary-container text-on-secondary-container';
  return (
    <div className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${toneClasses}`}>
      <span className="material-symbols-outlined text-[18px]">{icon}</span>
      {children}
    </div>
  );
}

function JoinPageNew() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const theme = getThemeVars(ACTIVE_THEME);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [joinInfo, setJoinInfo] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get('token');
  const currentPath = useMemo(
    () => getSafeRedirectPath(`${location.pathname}${location.search}`, '/'),
    [location.pathname, location.search]
  );

  const loginPath = buildLoginPath(currentPath);
  const registerPath = buildRegisterPath(currentPath);
  const isAuthenticated = Boolean(user || localStorage.getItem('accessToken'));

  useEffect(() => {
    let isMounted = true;

    const loadJoinInfo = async () => {
      if (!token) {
        if (isMounted) {
          setError(t('El enlace de ingreso no es válido.'));
          setLoading(false);
        }
        return;
      }

      try {
        const response = await joinService.getByToken(token);
        const data = response?.data;
        if (isMounted) setJoinInfo(data);
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || t('No se pudo cargar la información de la comunidad'));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadJoinInfo();
    return () => { isMounted = false; };
  }, [t, token]);

  const handleJoin = async () => {
    if (!token) return;
    setProcessing(true);
    setError('');

    try {
      const response = await joinService.joinByToken(token);
      if (response.status === 202) {
        // Pending approval — show informational message, do not redirect
        setSuccessMessage(t('Your join request was sent to the admins'));
      } else {
        // 201 or 200 — joined directly. Invalidamos el cache de la home
        // (productCards/recentActivity) además de las listas de comunidades
        // para que al volver al dashboard ya aparezcan los productos de la
        // comunidad recién unida.
        queryClient.invalidateQueries({ queryKey: ['userCommunities'] });
        queryClient.invalidateQueries({ queryKey: ['availableCommunities'] });
        queryClient.invalidateQueries({ queryKey: ['productCards'] });
        queryClient.invalidateQueries({ queryKey: ['recentActivity'] });
        setSuccessMessage(t('Te uniste a la comunidad. Redirigiendo...'));
        setTimeout(() => navigate('/communities'), 1500);
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate(loginPath);
        return;
      }
      setError(err?.response?.data?.message || t('Error al unirte a la comunidad'));
    } finally {
      setProcessing(false);
    }
  };

  const handleGoogleLogin = async () => {
    setProcessing(true);
    setError('');

    try {
      const response = await authService.getGoogleAuthUrl(currentPath);
      const payload = response?.data;
      if (!payload?.authUrl) throw new Error(t('No se pudo obtener la URL de autenticación'));
      window.location.href = payload.authUrl;
    } catch (err) {
      setError(err?.message || t('Error al iniciar sesión con Google'));
      setProcessing(false);
    }
  };

  const canBeUsed = joinInfo?.canBeUsed ?? joinInfo?.isCanBeUsed;
  const isActive = joinInfo?.active ?? joinInfo?.isActive;
  const isExpired = joinInfo?.expired ?? joinInfo?.isExpired;
  const hasReachedLimit = joinInfo?.hasReachedLimit ?? joinInfo?.isHasReachedLimit;

  const communityName = joinInfo?.communityName;
  const inviterName = joinInfo?.inviterName;
  const memberCount = joinInfo?.memberCount;
  const remainingUses = joinInfo?.remainingUses;
  const showLowSpots = remainingUses !== null && remainingUses !== undefined && remainingUses > 0 && remainingUses <= 5;

  const communityAvatarGradient = pickGradient(communityName);
  const inviterAvatarGradient = pickGradient(inviterName);

  return (
    <div
      className="dashboard-new font-body min-h-[100dvh] flex items-center justify-center px-4 py-10"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <div className="w-full max-w-md">
        <div className="bg-surface-container-lowest rounded-3xl shadow-xl overflow-hidden">
          {loading && (
            <div className="flex flex-col items-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
              <p className="text-on-surface-variant mt-3 text-sm">{t('Cargando comunidad...')}</p>
            </div>
          )}

          {!loading && error && !joinInfo && (
            <div className="px-8 py-12 flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-error-container flex items-center justify-center">
                <span className="material-symbols-outlined text-3xl text-on-error-container">link_off</span>
              </div>
              <h1 className="text-lg font-semibold text-on-surface">{t('No se pudo cargar la información de la comunidad')}</h1>
              <p className="text-sm text-on-surface-variant">{error}</p>
            </div>
          )}

          {!loading && joinInfo && (
            <>
              <div className="bg-gradient-to-b from-primary-container/40 to-transparent px-8 pt-10 pb-6 flex flex-col items-center text-center">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${communityAvatarGradient} flex items-center justify-center shadow-lg ring-4 ring-surface-container-lowest`}>
                  <span className="text-2xl font-extrabold text-white tracking-wide">
                    {getInitials(communityName)}
                  </span>
                </div>
                <p className="mt-5 text-xs uppercase tracking-[0.2em] font-bold text-on-surface-variant">
                  {t('Has sido invitado a unirte a')}
                </p>
                <h1 className="mt-2 text-2xl font-extrabold text-on-surface leading-tight">
                  {communityName}
                </h1>

                <div className="mt-4 flex items-center gap-2 flex-wrap justify-center text-sm text-on-surface-variant">
                  {memberCount !== undefined && memberCount !== null && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface-container-low font-semibold">
                      <span className="material-symbols-outlined text-[16px]">group</span>
                      {t('{{count}} miembros', { count: memberCount })}
                    </span>
                  )}
                  {joinInfo.communityDomain && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface-container-low font-semibold">
                      <span className="material-symbols-outlined text-[16px]">alternate_email</span>
                      {joinInfo.communityDomain}
                    </span>
                  )}
                </div>
              </div>

              <div className="px-8 pb-8 space-y-4">
                {inviterName && (
                  <div className="flex items-center gap-3 p-3 rounded-2xl bg-surface-container-low">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${inviterAvatarGradient} flex items-center justify-center shrink-0`}>
                      <span className="text-sm font-bold text-white">{getInitials(inviterName)}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] uppercase tracking-wider font-bold text-on-surface-variant">
                        {t('Invitado por')}
                      </p>
                      <p className="text-sm font-semibold text-on-surface truncate">{inviterName}</p>
                    </div>
                  </div>
                )}

                {error && (
                  <StatePill icon="error" tone="error">{error}</StatePill>
                )}

                {successMessage && (
                  <StatePill icon="check_circle" tone="primary">{successMessage}</StatePill>
                )}

                {!canBeUsed && isExpired && (
                  <StatePill icon="schedule" tone="error">
                    {t('Este enlace de ingreso expiró.')}
                  </StatePill>
                )}
                {!canBeUsed && !isExpired && hasReachedLimit && (
                  <StatePill icon="block" tone="error">
                    {t('Este enlace alcanzó su límite de usos.')}
                  </StatePill>
                )}
                {!canBeUsed && !isExpired && !hasReachedLimit && !isActive && (
                  <StatePill icon="link_off" tone="error">
                    {t('Este enlace no está activo.')}
                  </StatePill>
                )}
                {!canBeUsed && !isExpired && !hasReachedLimit && isActive && (
                  <StatePill icon="warning" tone="error">
                    {t('Este enlace no se puede usar en este momento.')}
                  </StatePill>
                )}

                {canBeUsed && !successMessage && !isAuthenticated && (
                  <div className="space-y-2 pt-1">
                    <p className="text-sm text-center text-on-surface-variant">
                      {t('Para unirte a esta comunidad, inicia sesión o regístrate.')}
                    </p>
                    <button
                      onClick={() => navigate(loginPath)}
                      disabled={processing}
                      className="w-full py-3 rounded-xl bg-primary text-on-primary font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                      {t('Iniciar Sesión')}
                    </button>
                    <button
                      onClick={() => navigate(registerPath)}
                      disabled={processing}
                      className="w-full py-3 rounded-xl border border-outline-variant/40 text-on-surface font-semibold hover:bg-surface-container-low transition-colors disabled:opacity-50"
                    >
                      {t('Registrarse')}
                    </button>
                    <button
                      onClick={handleGoogleLogin}
                      disabled={processing}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-outline-variant/40 text-on-surface font-semibold hover:bg-surface-container-low transition-colors disabled:opacity-50"
                    >
                      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      </svg>
                      {t('Continuar con Google')}
                    </button>
                  </div>
                )}

                {canBeUsed && !successMessage && isAuthenticated && (
                  <button
                    onClick={handleJoin}
                    disabled={processing}
                    className="w-full mt-1 py-3 rounded-xl bg-primary text-on-primary font-semibold flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {processing ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-on-primary border-t-transparent" />
                        {t('Uniéndote...')}
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[18px]">login</span>
                        {t('Unirse a la comunidad')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default JoinPageNew;
